import { memo, useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Tooltip,
} from '@fluentui/react-components';
import {
  CopyRegular,
  DeleteRegular,
  MoreHorizontalRegular,
  OpenRegular,
  SparkleRegular,
} from '@fluentui/react-icons';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

export interface NodeHoverToolbarProps {
  /** The trigger element (usually the FloatingNode) - must be a single React element */
  children: ReactElement;
  /** Callback when configure/open is clicked */
  onConfigure?: (() => void) | undefined;
  /** Callback when duplicate is clicked */
  onDuplicate?: (() => void) | undefined;
  /** Callback when delete is clicked */
  onDelete?: (() => void) | undefined;
  /** Callback when edit with AI is clicked */
  onEditWithAI?: (() => void) | undefined;
  /** Whether to show the toolbar (e.g., hide in monitoring mode) */
  showToolbar?: boolean | undefined;
  /** When true, toolbar won't show (e.g., when node is selected) */
  disabled?: boolean | undefined;
}

const useStyles = makeStyles({
  container: {
    position: 'relative',
    display: 'inline-block',
  },

  toolbar: {
    position: 'absolute',
    top: '-48px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '2px',
    boxShadow: tokens.shadow8,
    opacity: 0,
    visibility: 'hidden',
    transitionProperty: 'opacity, visibility',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    zIndex: 10,
    // Transparent bridge that fills the visual gap between the toolbar
    // and the node below it. Without this, the mouse exits the wrapper's
    // hit area while traveling from node → toolbar and the toolbar hides
    // before it can be reached.
    '::before': {
      content: '""',
      position: 'absolute',
      left: '0',
      right: '0',
      top: '100%',
      // Spans the gap from the toolbar's bottom edge down to the node.
      // 48px (top offset) is comfortably more than the toolbar's height,
      // so 24px is enough to overlap the node by a few pixels.
      height: '24px',
    },
  },

  toolbarVisible: {
    opacity: 1,
    visibility: 'visible',
  },

  iconButton: {
    minWidth: '24px',
    width: '24px',
    height: '24px',
    padding: '0',
  },

  deleteButton: {
    ':hover': {
      backgroundColor: tokens.colorPaletteRedBackground2,
      color: tokens.colorPaletteRedForeground1,
    },
  },
});

export const NodeHoverToolbar = memo(
  ({
    children,
    onConfigure,
    onDuplicate,
    onDelete,
    onEditWithAI,
    showToolbar = true,
    disabled = false,
  }: NodeHoverToolbarProps) => {
    const styles = useStyles();
    const isScopedEditEnabled = useFeatureFlag('scopedEdit');
    const [isHovered, setIsHovered] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearHideTimer = useCallback(() => {
      if (hideTimerRef.current !== null) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    }, []);

    const showImmediately = useCallback(() => {
      clearHideTimer();
      setIsHovered(true);
    }, [clearHideTimer]);

    // Defer hiding so a brief mouse exit (e.g. crossing the gap between
    // node and toolbar, or briefly leaving a button's hit area) doesn't
    // collapse the toolbar before the user can click an item.
    const scheduleHide = useCallback(() => {
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => {
        setIsHovered(false);
        hideTimerRef.current = null;
      }, 150);
    }, [clearHideTimer]);

    useEffect(() => clearHideTimer, [clearHideTimer]);

    // Show toolbar when hovered or menu is open (and not disabled)
    const showToolbarVisible = showToolbar && !disabled && (isHovered || isMenuOpen);

    // Check if we have any actions to show
    const hasQuickActions = onDuplicate || onDelete;
    const hasMenuActions = onConfigure || (onEditWithAI && isScopedEditEnabled);
    const hasAnyActions = hasQuickActions || hasMenuActions;

    if (!hasAnyActions) {
      return children;
    }

    return (
      <div
        className={styles.container}
        onMouseEnter={showImmediately}
        onMouseLeave={scheduleHide}
        onFocusCapture={showImmediately}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            scheduleHide();
          }
        }}
      >
        {showToolbar && (
          <div
            className={mergeClasses(styles.toolbar, showToolbarVisible && styles.toolbarVisible)}
            role="toolbar"
            aria-label="Node actions"
          >
            {onDuplicate && (
              <Tooltip content="Duplicate" relationship="label" positioning="above">
                <Button
                  className={styles.iconButton}
                  appearance="subtle"
                  size="small"
                  icon={<CopyRegular />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate();
                  }}
                  aria-label="Duplicate node"
                />
              </Tooltip>
            )}

            {onDelete && (
              <Tooltip content="Delete" relationship="label" positioning="above">
                <Button
                  className={mergeClasses(styles.iconButton, styles.deleteButton)}
                  appearance="subtle"
                  size="small"
                  icon={<DeleteRegular />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  aria-label="Delete node"
                />
              </Tooltip>
            )}

            {hasMenuActions && (
              <Menu
                open={isMenuOpen}
                onOpenChange={(_, data) => setIsMenuOpen(data.open)}
              >
                <MenuTrigger disableButtonEnhancement>
                  <Tooltip content="More actions" relationship="label" positioning="above">
                    <Button
                      className={styles.iconButton}
                      appearance="subtle"
                      size="small"
                      icon={<MoreHorizontalRegular />}
                      onClick={(e) => e.stopPropagation()}
                      aria-label="More actions"
                    />
                  </Tooltip>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {onConfigure && (
                      <MenuItem
                        icon={<OpenRegular />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onConfigure();
                          setIsMenuOpen(false);
                        }}
                      >
                        Open
                      </MenuItem>
                    )}
                    {onEditWithAI && isScopedEditEnabled && (
                      <MenuItem
                        icon={<SparkleRegular />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditWithAI();
                          setIsMenuOpen(false);
                        }}
                      >
                        Edit with AI
                      </MenuItem>
                    )}
                  </MenuList>
                </MenuPopover>
              </Menu>
            )}
          </div>
        )}
        {children}
      </div>
    );
  }
);

NodeHoverToolbar.displayName = 'NodeHoverToolbar';
